import { useEffect, useState } from "react";
import { Button, Input, Modal, DialogFooter, Loader } from "@/ui/atoms";
import { FormField } from "@/ui/molecules";

export type TSegmentCustomValueDraft = {
    account_code: string;
    code: string;
    description: string;
    property_code: string | null;
    is_active: boolean;
};

export type TSegmentCustomValueFormModalProps = {
    open: boolean;
    mode: "create" | "edit";
    segmentType: number;
    initial?: TSegmentCustomValueDraft;
    isSaving?: boolean;
    error?: string | null;
    masterValues?: Array<{ code: string; description: string }>;
    onSubmit: (draft: TSegmentCustomValueDraft) => void;
    onClose: () => void;
};

const emptyDraft = (): TSegmentCustomValueDraft => ({
    account_code: "",
    code: "",
    description: "",
    property_code: null,
    is_active: true,
});

export function SegmentCustomValueFormModal({
    open,
    mode,
    segmentType,
    initial,
    isSaving = false,
    error,
    masterValues = [],
    onSubmit,
    onClose,
}: TSegmentCustomValueFormModalProps) {
    const [draft, setDraft] = useState<TSegmentCustomValueDraft>(initial ?? emptyDraft());

    // Re-seed the form whenever the modal (re)opens for a different row.
    useEffect(() => {
        if (open) setDraft(initial ?? emptyDraft());
    }, [open, initial]);

    const propCodeRequired = segmentType === 2;
    const useMasterSelect = segmentType >= 4;

    const isValid =
        draft.account_code.trim() !== "" &&
        draft.code.trim() !== "" &&
        (!propCodeRequired || (draft.property_code ?? "").trim() !== "");

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (!isValid) return;
        onSubmit({
            account_code: draft.account_code.trim(),
            code: draft.code.trim(),
            description: draft.description.trim(),
            property_code: propCodeRequired ? (draft.property_code ?? "").trim() || null : null,
            is_active: draft.is_active,
        });
    };

    const handleMasterCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedCode = e.target.value;
        const master = masterValues.find((m) => m.code === selectedCode);
        setDraft({
            ...draft,
            code: selectedCode,
            description: master?.description ?? "",
        });
    };

    return (
        <Modal
            open={open}
            onOpenChange={(next) => !next && onClose()}
            title={
                mode === "create"
                    ? `Add Segment ${segmentType} Custom Value`
                    : `Edit Segment ${segmentType} Custom Value`
            }
            description="Account, code, property code, and status."
        >
            <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                    <div className="rounded-lg bg-destructive/10 p-3 text-destructive text-sm">
                        {error}
                    </div>
                )}
                <FormField label="Account" htmlFor="custom-account" required>
                    <Input
                        id="custom-account"
                        aria-label="Account"
                        value={draft.account_code}
                        onChange={(e) => setDraft({ ...draft, account_code: e.target.value })}
                        placeholder="Account code"
                    />
                </FormField>
                <FormField label="Platform Value" htmlFor="custom-code" required>
                    {useMasterSelect ? (
                        <select
                            id="custom-code"
                            aria-label="Platform Value"
                            value={draft.code}
                            onChange={handleMasterCodeChange}
                            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <option value="">Select a value</option>
                            {draft.code !== "" &&
                                !masterValues.some((m) => m.code === draft.code) && (
                                    <option value={draft.code}>{`${draft.code} (current)`}</option>
                                )}
                            {masterValues.map((m) => (
                                <option key={m.code} value={m.code}>
                                    {m.description ? `${m.code} — ${m.description}` : m.code}
                                </option>
                            ))}
                        </select>
                    ) : (
                        <Input
                            id="custom-code"
                            aria-label="Platform Value"
                            value={draft.code}
                            onChange={(e) => setDraft({ ...draft, code: e.target.value })}
                            placeholder="Platform Value"
                        />
                    )}
                </FormField>
                <FormField label="Description" htmlFor="custom-description">
                    <Input
                        id="custom-description"
                        aria-label="Description"
                        value={draft.description}
                        onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                        placeholder="Optional description"
                    />
                </FormField>
                {propCodeRequired && (
                    <FormField label="Property code" htmlFor="custom-propcode" required>
                        <Input
                            id="custom-propcode"
                            aria-label="Property code"
                            value={draft.property_code ?? ""}
                            onChange={(e) =>
                                setDraft({ ...draft, property_code: e.target.value || null })
                            }
                            placeholder="Required for segment 2"
                        />
                    </FormField>
                )}
                <label className="inline-flex items-center gap-2">
                    <input
                        type="checkbox"
                        aria-label="Active"
                        checked={draft.is_active}
                        onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })}
                    />
                    <span>Active</span>
                </label>
                <DialogFooter className="gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={isSaving || !isValid}>
                        {isSaving ? (
                            <>
                                <Loader size="sm" className="mr-2" />
                                Saving...
                            </>
                        ) : mode === "create" ? (
                            "Create"
                        ) : (
                            "Save"
                        )}
                    </Button>
                </DialogFooter>
            </form>
        </Modal>
    );
}
